import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FETCH_SIZE_CARD_DATA } from "../../DataTagConstants";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
import "./DataEstateSizeCard.scss";
import { filesize } from "filesize";
import { numberFormatter } from "../../../../components/common/Dashboard/utils";

const GREY = "#e5e5e5";

const COLORS = {
  PROCESSED: "#4caf50",
  SENSITIVE: "#ff3366",
  NO_RISK: "#9b59b6",
  REGULATED: "#ffa500",
  PUBLIC: "#0090ff",
};

const LABELS = {
  PROCESSED: "Processed",
  NOT_PROCESSED: "Not Processed",
  SENSITIVE: "Sensitive",
  NO_RISK: "No Risk",
  REGULATED: "Regulated",
  NOT_REGULATED: "Remaining",
  PUBLIC: "Public",
  NOT_PUBLIC: "Remaining",
};

const pct = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? v : Number(v.toFixed(2));
};

const getOrCreateTooltipEl = (wrapper) => {
  let el = wrapper.querySelector(".de-chart-tooltip");
  if (!el) {
    el = document.createElement("div");
    el.className = "de-chart-tooltip";
    wrapper.appendChild(el);
  }
  return el;
};

const externalTooltipHandler = (context) => {
  const { chart, tooltip } = context;
  const wrapper = chart.canvas.closest(".data-estate-chart");
  if (!wrapper) return;
  const tooltipEl = getOrCreateTooltipEl(wrapper);
  if (tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }
  if (tooltip.body) {
    const lines = tooltip.body.map((b) => b.lines).flat();
    tooltipEl.innerHTML = lines.map((line) => `<div>${line}</div>`).join("");
  }
  const { offsetLeft: canvasX, offsetTop: canvasY } = chart.canvas;
  tooltipEl.style.opacity = 1;
  tooltipEl.style.left = `${canvasX + tooltip.caretX}px`;
  tooltipEl.style.top = `${canvasY + tooltip.caretY}px`;
};

// ring = [{label, count, percent}] -> chart.js dataset (count shown in the tooltip, percent drives the arc)
const buildRing = (ring = [], extra = {}) => ({
  labels: ring.map((r) => LABELS[r.label] || r.label),
  counts: ring.map((r) => Number(r.count) || 0),
  data: ring.map((r) => Number(r.percent) || 0),
  backgroundColor: ring.map((r) => COLORS[r.label] || GREY),
  weight: 1,
  spacing: 4,
  ...extra,
});

const tooltipLabel = (context) => {
  const ds = context.dataset;
  const label = ds.labels?.[context.dataIndex] || context.label || "";
  return `${label}: ${numberFormatter(ds.counts?.[context.dataIndex] ?? 0)}`;
};

const chartOptions = (cutout, isNTFSPublic) => ({
  responsive: true,
  maintainAspectRatio: false,
  devicePixelRatio: window.devicePixelRatio || 1,
  resizeDelay: 0,
  cutout,
  plugins: {
    legend: { display: false },
    datalabels: { display: false },
    tooltip: {
      enabled: false,
      external: externalTooltipHandler,
      callbacks: {
        label: isNTFSPublic ? () => "Public: N/A" : tooltipLabel,
      },
    },
  },
  interaction: { mode: "nearest", intersect: true },
});

function DataEstateSizeCard() {
  const dispatch = useDispatch();

  const dataSourceType = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.type,
  );
  const dataSourceId = useSelector(
    (state) => state.dataTagReducer.selectedDataSource?.id,
  );
  const isDataBase = useSelector((state) => state.dataTagReducer.isDatabase);
  const overview = useSelector((state) => state.dataTagReducer.dataEstateOverview);
  const sizeData = useSelector((state) => state.dataTagReducer.sizeCardData);

  const isNTFS = dataSourceType === "NTFS";
  const card2 = overview?.card2;

  // the overview API drives file data sources; the old size API is only needed for databases
  useEffect(() => {
    if (dataSourceId && isDataBase) {
      dispatch({ type: FETCH_SIZE_CARD_DATA, payload: dataSourceId });
    }
  }, [dataSourceId, isDataBase]);

  const view = useMemo(() => {
    if (!isDataBase) {
      if (!card2) return null;
      return {
        total: Number(card2.totalFiles) || 0,
        sizeText: filesize((Number(card2.totalSizeGb) || 0) * 1024 ** 3, {
          standard: "jedec",
        }),
        sensitivePct: pct(card2.sensitivePercent),
        regulatedPct: pct(card2.regulatedPercent),
        publicPct: pct(card2.publicPercent),
        processing: card2.processingRing,
        sensitivity: card2.sensitivityRing,
        regulation: card2.regulationRing,
        publicRing: card2.publicRing,
      };
    }

    // database: keep the previous behaviour (columns / tables)
    if (!sizeData) return null;
    const total = Number(sizeData?.totalColumns?.[0]?.["DataguardDbViewTotal.totalColumns"] || 0);
    const sensitive = Number(sizeData?.sensitiveColumns?.[0]?.["DataguardDbViewTotal.sensitiveColumns"] || 0);
    const regulated = Number(sizeData?.regulatedTables?.[0]?.["DataguardDbViewTotal.regulatedTables"] || 0);
    const p = (n) => (total > 0 ? (n / total) * 100 : 0);
    const ring = (a, b, c, d) => [
      { label: a, count: c, percent: p(c) },
      { label: b, count: Math.max(0, total - c), percent: Math.max(0, 100 - p(c)) },
    ];
    return {
      total,
      sizeText: "",
      sensitivePct: pct(p(sensitive)),
      regulatedPct: pct(p(regulated)),
      publicPct: pct(p(total)),
      processing: ring("PROCESSED", "NOT_PROCESSED", 0),
      sensitivity: ring("SENSITIVE", "NOT_PROCESSED", sensitive),
      regulation: ring("REGULATED", "NOT_REGULATED", regulated),
      publicRing: ring("PUBLIC", "NOT_PUBLIC", 0),
    };
  }, [isDataBase, card2, sizeData]);

  if (!view) {
    return <div>Loading...</div>;
  }

  const doughnutData = {
    datasets: [
      buildRing(view.processing),
      buildRing(view.sensitivity),
      buildRing(view.regulation),
    ],
  };

  const pieRing = buildRing(view.publicRing, { weight: undefined, spacing: 0 });
  const publicPieData = {
    labels: pieRing.labels,
    datasets: [{ ...pieRing, borderWidth: 0, cutout: "65%" }],
  };

  return (
    <div className="size-card">
      <div className="card-header size">
        <div>{isDataBase ? "Schema" : "Size"}</div>
        {!isDataBase && <div className="size-display">{view.sizeText}</div>}
      </div>

      <div className="sizeCardLowerContainer">
        <div className="details">
          <div className="row">
            <div className="circle total"></div>
            <span>Total</span>
            <strong>{numberFormatter(view.total)}</strong>
          </div>
          <div className="row">
            <div className="circle sensitive"></div>
            <span>Sensitive</span>
            <strong>{view.sensitivePct}%</strong>
          </div>
          <div className="row">
            <div className="circle regulated"></div>
            <span>Regulated</span>
            <strong>{view.regulatedPct}%</strong>
          </div>
          <div className="row">
            <div className="circle pda"></div>
            <span>Public</span>
            <strong>{isNTFS ? "N/A" : `${view.publicPct}%`}</strong>
          </div>
        </div>

        <div className="chart data-estate-chart">
          <Doughnut data={doughnutData} options={chartOptions("42%", false)} />
          <div className="processing-chart">
            <Doughnut data={publicPieData} options={chartOptions("0%", isNTFS)} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DataEstateSizeCard;